import { updateActionItemStatusForItem } from "../../businessModeService";
import { getItemStableId, isPendingManualActionItem } from "../actionItemHelpers";

/**
 * When refreshing from the server, keep local rows that have in-flight mutations,
 * including optimistic manual adds that the server snapshot does not contain yet.
 *
 * @example
 * // Input — in-flight status change on a row the server already has
 * mergeItemsPreservingPendingMutations(
 *   [{ key: "a", status: "done" }],
 *   [{ key: "a", status: "pending" }],
 *   new Set(["a"]),
 *   getItemStableId
 * );
 * // Output: [{ key: "a", status: "done" }]
 *
 * @example
 * // Input — optimistic manual add not in the server snapshot yet
 * mergeItemsPreservingPendingMutations(
 *   [{ file_id: "manual::pending-1", action_item: "Email legal" }],
 *   [{ key: "existing", action_item: "Older task" }],
 *   new Set(["manual::pending-1"])
 * );
 * // Output: pending row first, then the server row
 */
export const mergeItemsPreservingPendingMutations = (
  prevItems,
  serverItems,
  pendingIds,
  getStableId = getItemStableId
) => {
  const sorted = Array.isArray(serverItems) ? serverItems : [];
  const prev = Array.isArray(prevItems) ? prevItems : [];
  const protectedIds = pendingIds instanceof Set ? pendingIds : new Set();
  const prevById = new Map(prev.map((item) => [getStableId(item), item]));
  const merged = sorted.map((serverItem) => {
    const id = getStableId(serverItem);
    const prevItem = prevById.get(id);
    if (protectedIds.has(id)) {
      return prevItem ?? serverItem;
    }
    // Keep the client key so a later refresh does not remount a just-added row.
    const clientKey = String(prevItem?._clientKey || "").trim();
    if (clientKey && !String(serverItem?._clientKey || "").trim()) {
      return { ...serverItem, _clientKey: clientKey };
    }
    return serverItem;
  });

  const serverIds = new Set(merged.map((item) => getStableId(item)));
  const extras = prev.filter((item) => {
    const id = getStableId(item);
    if (!id || serverIds.has(id)) return false;
    return protectedIds.has(id) || isPendingManualActionItem(item);
  });
  if (extras.length === 0) return merged;
  return [...extras, ...merged];
};

/**
 * Keep the previous object (and array) identity for rows whose content did not change, so a
 * background poll that returns identical data triggers no re-render of a thousand-row list.
 *
 * @example
 * const prev = [{ key: "a", status: "pending" }];
 * reuseUnchangedItems(prev, [{ key: "a", status: "pending" }]) === prev;
 * // true
 */
export const reuseUnchangedItems = (prevItems, nextItems, getStableId = getItemStableId) => {
  const prev = Array.isArray(prevItems) ? prevItems : [];
  const next = Array.isArray(nextItems) ? nextItems : [];
  if (prev.length === 0) return next;
  const prevById = new Map();
  prev.forEach((item) => prevById.set(getStableId(item), item));
  let allSame = prev.length === next.length;
  const reused = next.map((item, index) => {
    const old = prevById.get(getStableId(item));
    let same = false;
    if (old === item) {
      same = true;
    } else if (old) {
      try {
        same = JSON.stringify(old) === JSON.stringify(item);
      } catch (_) {
        same = false;
      }
    }
    if (!same || prev[index] !== old) allSame = false;
    return same ? old : item;
  });
  return allSame ? prev : reused;
};

/**
 * Update one action item's status in local state immediately, then persist in the background.
 *
 * @example
 * // Input
 * void runOptimisticActionItemStatusChange({
 *   item: { key: "abc", status: "pending" },
 *   newStatus: "done",
 *   setItems,
 *   registerMutation,
 *   unregisterMutation,
 * });
 * // Output: list shows "done" instantly; PATCH runs async
 */
export const runOptimisticActionItemStatusChange = ({
  item,
  newStatus,
  setItems,
  registerMutation = () => {},
  unregisterMutation = () => {},
  refreshItems,
  onError,
  isMounted = () => true,
  getStableId = getItemStableId,
}) => {
  const stableId = getStableId(item);
  const previousStatus = item?.status;
  if (!stableId || previousStatus === newStatus) {
    return Promise.resolve();
  }

  registerMutation(stableId);
  setItems((prev) =>
    prev.map((entry) =>
      getStableId(entry) === stableId ? { ...entry, status: newStatus } : entry
    )
  );

  return updateActionItemStatusForItem(item, newStatus)
    .then(async () => {
      if (refreshItems) {
        // Await so unregister stays deferred until this refresh applies; otherwise an
        // overlapping silent poll can briefly paint pre-mutation server state.
        await refreshItems().catch(() => {});
      }
    })
    .catch((err) => {
      if (isMounted()) {
        setItems((prev) =>
          prev.map((entry) =>
            getStableId(entry) === stableId
              ? { ...entry, status: previousStatus }
              : entry
          )
        );
        if (onError) onError(err);
      }
    })
    .finally(() => {
      unregisterMutation(stableId);
    });
};

/**
 * Optimistically update many action items at once, then persist each in parallel.
 *
 * @example
 * // Input
 * void runOptimisticBulkActionItemStatusChange({
 *   targets: [{ key: "a", status: "pending" }, { key: "b", status: "pending" }],
 *   newStatus: "done",
 *   setItems,
 * });
 * // Output: both rows leave the pending view immediately
 */
export const runOptimisticBulkActionItemStatusChange = ({
  targets,
  newStatus,
  setItems,
  registerMutation = () => {},
  unregisterMutation = () => {},
  refreshItems,
  onError,
  isMounted = () => true,
  getStableId = getItemStableId,
}) => {
  const snapshots = (Array.isArray(targets) ? targets : [])
    .map((item) => ({
      item,
      stableId: getStableId(item),
      previousStatus: item?.status,
    }))
    .filter(({ stableId, previousStatus }) => stableId && previousStatus !== newStatus);

  if (snapshots.length === 0) {
    return Promise.resolve();
  }

  snapshots.forEach(({ stableId }) => registerMutation(stableId));
  const targetIds = new Set(snapshots.map(({ stableId }) => stableId));
  setItems((prev) =>
    prev.map((entry) =>
      targetIds.has(getStableId(entry)) ? { ...entry, status: newStatus } : entry
    )
  );

  return Promise.all(
    snapshots.map(({ item, stableId, previousStatus }) =>
      updateActionItemStatusForItem(item, newStatus).catch((err) => ({
        err,
        stableId,
        previousStatus,
      }))
    )
  )
    .then(async (results) => {
      const failures = results.filter((result) => result?.err);
      if (failures.length > 0 && isMounted()) {
        const revertById = new Map(
          failures.map(({ stableId, previousStatus }) => [stableId, previousStatus])
        );
        setItems((prev) =>
          prev.map((entry) => {
            const id = getStableId(entry);
            if (revertById.has(id)) {
              return { ...entry, status: revertById.get(id) };
            }
            return entry;
          })
        );
        if (onError) onError(failures[0].err);
      }
      if (refreshItems) {
        await refreshItems().catch(() => {});
      }
    })
    .finally(() => {
      snapshots.forEach(({ stableId }) => unregisterMutation(stableId));
    });
};
